export function getSwarmWorkflowSteps(swarmEvents, isSwarmRunning) {
  if (!swarmEvents) return [];

  const steps = [];
  const agentCounts = {};
  let initialized = false;
  let completed = false;
  let stopped = false;
  let paused = false;
  let webSearchPhase = null;

  swarmEvents.forEach(e => {
    if (e.type === 'session_started') initialized = true;
    if (e.type === 'session_completed') completed = true;
    if (e.type === 'session_stopped') stopped = true;
    if (e.type === 'state_changed' && e.payload?.to === 'paused') paused = true;
    if (e.type === 'state_changed' && e.payload?.to === 'running') paused = false;
    if (e.type === 'tool_call' && e.payload?.tool === 'web_search') {
      const phase = e.payload?.phase;
      if (phase === 'started') webSearchPhase = 'started';
      if (phase === 'research_update_wait') webSearchPhase = 'research_update_wait';
    }
    if (e.type === 'tool_return' && e.payload?.tool === 'web_search') {
      webSearchPhase = e.payload?.phase || 'completed';
    }
    if (e.type === 'agent_message') {
      const id = e.payload?.speaker_id?.toUpperCase() || "UNKNOWN";
      agentCounts[id] = (agentCounts[id] || 0) + 1;
    }
  });

  if (initialized) {
    steps.push({ id: 'init', message: "Debate session initialized", status: 'done' });
  }

  if (webSearchPhase) {
    const webSearchMessageMap = {
      started: 'Searching web...',
      deferred: 'Continuing debate while web search runs in background',
      completed: 'Web search complete',
      injected: 'Background web search injected into debate context',
      research_update_wait: 'Waiting briefly for background research update...',
      research_update_injected: 'Background research update injected',
      research_update_timeout: 'No research update yet; debate continues',
      failed: 'Web search unavailable; debate continues without it',
      discarded_terminal: 'Session ended before web search returned',
      injected_terminal: 'Late web research arrived after session end',
    };

    const donePhases = new Set([
      'completed', 'injected', 'research_update_injected',
      'research_update_timeout', 'failed', 'discarded_terminal', 'injected_terminal',
    ]);

    steps.push({
      id: 'web-search',
      message: webSearchMessageMap[webSearchPhase] || 'Web search update',
      status: donePhases.has(webSearchPhase) ? 'done' : 'busy',
    });
  }

  if (paused && !completed && !stopped) {
    steps.push({ id: 'paused', message: 'Paused - waiting for resume', status: 'busy' });
  }

  Object.entries(agentCounts).forEach(([name, count]) => {
    steps.push({
      id: `agent-${name}`,
      message: `Agent ${name}: ${count} contribution${count > 1 ? 's' : ''}`,
      status: isSwarmRunning ? 'busy' : 'done',
    });
  });

  if (completed) {
    steps.push({ id: 'completed', message: "Debate reached natural conclusion", status: 'done' });
  } else if (stopped) {
    steps.push({ id: 'stopped', message: "Session terminated by user", status: 'done' });
  }

  return steps;
}

export function getSwarmActionButtonProps({ isSwarmRunning, isSwarmTerminal, isSwarmStopping, isMobileView, onStop, askButtonStyle = {} }) {
  return {
    type: (isSwarmRunning && !isSwarmTerminal) || isSwarmStopping ? "button" : "submit",
    onClick: isSwarmStopping ? undefined : (
      (isSwarmRunning && !isSwarmTerminal) ? onStop : undefined
    ),
    disabled: isSwarmStopping,
    className: "themed-button",
    style: {
      ...askButtonStyle,
      ...(isMobileView && {
        minWidth: (isSwarmRunning && !isSwarmTerminal) ? "40px" : "100px",
        padding: (isSwarmRunning && !isSwarmTerminal) ? "0" : "0 0.75rem",
      }),
      ...((isSwarmRunning && !isSwarmTerminal) && {
        backgroundColor: "var(--color-danger, #dc3545)",
        cursor: "pointer",
        opacity: 1,
      }),
      ...(isSwarmStopping && {
        backgroundColor: "var(--color-danger, #dc3545)",
        cursor: "not-allowed",
        opacity: 0.7,
      }),
    },
    title: (isSwarmRunning && !isSwarmTerminal) ? "Stop the swarm debate" : "Start the swarm debate",
  };
}

export function getSwarmActionButtonContent(isSwarmRunning, isSwarmTerminal, isMobile) {
  if (isSwarmRunning && !isSwarmTerminal) {
    return isMobile ? "X" : "Stop";
  }
  return "Start Swarm";
}
